import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Terminal, Search, AlertTriangle, XCircle, Info, AlertOctagon, CheckCircle2, Download, ArrowDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { explainLog } from "@/lib/logExplain";
import { toast } from "sonner";

const SEVERITY_CONFIG = {
  info: { icon: Info, color: 'text-[hsl(var(--ops-brand))]', bg: 'bg-[hsl(var(--ops-brand))]/10', label: 'INFO' },
  warning: { icon: AlertTriangle, color: 'text-[hsl(var(--ops-warn))]', bg: 'bg-[hsl(var(--ops-warn))]/10', label: 'WARN' },
  error: { icon: XCircle, color: 'text-[hsl(var(--ops-short))]', bg: 'bg-[hsl(var(--ops-short))]/10', label: 'ERROR' },
  critical: { icon: AlertOctagon, color: 'text-[hsl(var(--ops-critical))]', bg: 'bg-[hsl(var(--ops-critical))]/10', label: 'CRIT' },
};

// Tone of the plain-English explanation (from explainLog) -> icon + colour.
const TONE_CONFIG = {
  info: { icon: Info, color: 'text-[hsl(var(--ops-brand))]' },
  good: { icon: CheckCircle2, color: 'text-[hsl(var(--ops-long))]' },
  warning: { icon: AlertTriangle, color: 'text-[hsl(var(--ops-warn))]' },
  problem: { icon: XCircle, color: 'text-[hsl(var(--ops-short))]' },
};

// Follow ("stick to bottom") only while the reader is within this many px of the bottom.
const FOLLOW_THRESHOLD_PX = 40;
// While paused, older lines are kept (not trimmed) so what you're reading never disappears.
const PAUSED_MAX_LINES = 2000;
const LIVE_LIMIT = 200;

const logKey = (log) => log.log_id || `${log.timestamp || ''}|${log.source || ''}|${log.message || ''}`;
const logTime = (log) => { const t = log.timestamp ? Date.parse(log.timestamp) : NaN; return Number.isNaN(t) ? 0 : t; };

export default function SystemLogs() {
  const { autoRefresh } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');
  // autoScroll = "following the newest line". Scrolling up turns it off by itself; the
  // switch and the "new logs" pill turn it back on.
  const [autoScroll, setAutoScroll] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const scrollRef = useRef(null);
  const followRef = useRef(true);        // mirrors autoScroll synchronously for the poll + scroll handlers
  const manualPauseRef = useRef(false);  // switch turned off by hand: don't re-follow just by reaching the bottom
  const logsRef = useRef([]);
  const anchorRef = useRef(null);        // {key, offset} of the line at the top of the view, captured before a paused update
  const programmaticRef = useRef(false);
  const touchYRef = useRef(null);
  const [expanded, setExpanded] = useState(() => new Set()); // row keys whose explanation is open
  const openedRef = useRef(null);        // row just opened: make sure its explanation is in view

  const getViewport = () => scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]') || null;

  const setFollow = useCallback((on) => {
    followRef.current = on;
    setAutoScroll(on);
    if (on) { setNewCount(0); setAwayFromBottom(false); }
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = getViewport();
    if (!el) return;
    programmaticRef.current = true;
    el.scrollTop = el.scrollHeight;
    requestAnimationFrame(() => { programmaticRef.current = false; });
  }, []);

  // Remember which line is at the top of the view and where, so a paused update can put it back exactly.
  const captureAnchor = useCallback(() => {
    const el = getViewport();
    if (!el) { anchorRef.current = null; return; }
    const top = el.getBoundingClientRect().top;
    const rows = el.querySelectorAll('[data-log-key]');
    for (const row of rows) {
      const r = row.getBoundingClientRect();
      if (r.bottom > top + 1) { anchorRef.current = { key: row.getAttribute('data-log-key'), offset: r.top - top }; return; }
    }
    anchorRef.current = null;
  }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (severity) params.set('severity', severity);
      if (search) params.set('search', search);
      params.set('limit', String(LIVE_LIMIT));
      const [logsRes, alertsRes] = await Promise.all([
        api.get(`/ops/logs?${params.toString()}`),
        api.get('/ops/alerts?acknowledged=false&limit=20'),
      ]);
      const fresh = (logsRes.data.logs || []).slice().reverse(); // oldest first, newest at the bottom
      const prev = logsRef.current;
      if (followRef.current || prev.length === 0) {
        anchorRef.current = null;
        logsRef.current = fresh;
        setLogs(fresh);
      } else {
        // Paused: keep every line already on screen, add only the new ones, and remember the anchor line.
        const seen = new Set(prev.map(logKey));
        const added = fresh.filter((l) => !seen.has(logKey(l)));
        if (added.length) {
          captureAnchor();
          let merged = prev.concat(added);
          const lastPrevTime = prev.length ? logTime(prev[prev.length - 1]) : 0;
          if (added.some((l) => logTime(l) < lastPrevTime)) {
            merged = merged.map((l, i) => [l, i]).sort((a, b) => (logTime(a[0]) - logTime(b[0])) || (a[1] - b[1])).map((x) => x[0]);
          }
          if (merged.length > PAUSED_MAX_LINES) merged = merged.slice(merged.length - PAUSED_MAX_LINES);
          logsRef.current = merged;
          setLogs(merged);
          setNewCount((n) => n + added.length);
        }
      }
      setCounts(logsRes.data.counts || {});
      setAlerts(alertsRes.data.alerts || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [severity, search, captureAnchor]);

  // A new filter/search is a new list: start at the newest line, following.
  useEffect(() => {
    manualPauseRef.current = false;
    logsRef.current = [];
    setFollow(true);
  }, [severity, search, setFollow]);

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load, autoRefresh]);

  // After each update: following -> stick to the bottom; paused -> put the anchor line back where it was.
  useLayoutEffect(() => {
    const el = getViewport();
    if (!el) return;
    if (followRef.current) {
      if (touchYRef.current == null) scrollToBottom(); // never yank the list out from under a finger
      return;
    }
    const a = anchorRef.current;
    anchorRef.current = null;
    if (!a) return;
    const row = el.querySelector(`[data-log-key="${CSS.escape(a.key)}"]`);
    if (!row) return;
    const delta = (row.getBoundingClientRect().top - el.getBoundingClientRect().top) - a.offset;
    if (Math.abs(delta) >= 1) { programmaticRef.current = true; el.scrollTop += delta; requestAnimationFrame(() => { programmaticRef.current = false; }); }
  }, [logs, scrollToBottom]);

  const hasViewer = !loading && logs.length > 0;

  // Watch the reader: scrolling up pauses following; coming back to the bottom resumes it.
  useEffect(() => {
    if (!hasViewer) return;
    const el = getViewport();
    if (!el) return;
    const distance = () => el.scrollHeight - el.scrollTop - el.clientHeight;
    const pause = () => { if (followRef.current && el.scrollHeight > el.clientHeight + 1) setFollow(false); };
    const onScroll = () => {
      const away = distance() > FOLLOW_THRESHOLD_PX;
      setAwayFromBottom(away);
      if (programmaticRef.current) return;
      if (away) pause();
      else if (!followRef.current && !manualPauseRef.current) setFollow(true);
    };
    const onWheel = (e) => { if (e.deltaY < 0) pause(); };
    const onTouchStart = (e) => { touchYRef.current = e.touches[0]?.clientY ?? null; };
    const onTouchMove = (e) => {
      const y = e.touches[0]?.clientY;
      if (touchYRef.current != null && y != null && y - touchYRef.current > 6) pause(); // finger moving down = reading older lines
    };
    const onTouchEnd = () => { touchYRef.current = null; };
    const onKey = (e) => { if (['ArrowUp', 'PageUp', 'Home'].includes(e.key)) pause(); };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    el.addEventListener('keydown', onKey);
    if (followRef.current) scrollToBottom();
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('keydown', onKey);
    };
  }, [hasViewer, setFollow, scrollToBottom]);

  // Back to the newest line and follow again (the pill, or switching Auto-scroll on).
  const jumpToLatest = useCallback(() => {
    manualPauseRef.current = false;
    setFollow(true);
    const trimmed = logsRef.current.length > LIVE_LIMIT ? logsRef.current.slice(-LIVE_LIMIT) : logsRef.current;
    if (trimmed !== logsRef.current) { logsRef.current = trimmed; setLogs(trimmed); }
    requestAnimationFrame(scrollToBottom);
    scrollToBottom();
  }, [setFollow, scrollToBottom]);

  // Tap / click / Enter / Space on a line opens its plain-English explanation below it.
  const toggleLine = useCallback((key, isOpen) => {
    if (!isOpen) {
      openedRef.current = key;
      // Opening a line means reading it: stop following so the next refresh doesn't pull the list away.
      if (followRef.current) setFollow(false);
    }
    setExpanded((prev) => { const next = new Set(prev); if (isOpen) next.delete(key); else next.add(key); return next; });
  }, [setFollow]);

  useLayoutEffect(() => {
    const key = openedRef.current;
    openedRef.current = null;
    const el = getViewport();
    if (!key || !el) return;
    const row = el.querySelector(`[data-log-key="${CSS.escape(key)}"]`);
    if (!row) return;
    const view = el.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const over = r.bottom - view.bottom;
    if (over > 0) el.scrollTop += Math.min(over + 4, r.top - view.top); // never push the line itself off the top
  }, [expanded]);

  const onAutoScrollChange = (on) => {
    if (on) jumpToLatest();
    else { manualPauseRef.current = true; setFollow(false); }
  };

  // Stable, unique row keys (a repeated identical line gets a #n suffix).
  const rowKeys = (() => { const seen = new Map(); return logs.map((l) => { const k = logKey(l); const n = seen.get(k) || 0; seen.set(k, n + 1); return n ? `${k}#${n}` : k; }); })();

  const showPill = hasViewer && !autoScroll && (newCount > 0 || awayFromBottom);

  const ackAlert = async (alertId) => {
    try {
      await api.put(`/ops/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      load();
    } catch (e) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const exportCSV = () => {
    if (logs.length === 0) { toast.warning('No logs to export'); return; }
    const headers = ['Timestamp','Severity','Source','Message'];
    const csvRows = [headers.join(',')];
    logs.forEach(log => {
      const ts = log.timestamp ? new Date(log.timestamp).toISOString() : '';
      const line = [
        `"${ts}"`, log.severity || 'info', log.source || '',
        `"${(log.message || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(line.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mentorai_logs_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${logs.length} log entries`);
  };

  return (
    <div data-testid="ops-logs-page" className="space-y-4">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">System / Logs</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">Real-time MentorAI system activity · tap a line to see what it means</p>
      </div>

      <div className="grid gap-4 max-lg:grid-cols-1 lg:grid-cols-[1fr_300px]">
        {/* Main log viewer */}
        <div className="space-y-3 max-lg:min-w-0">
          {/* Controls */}
          <Card className="p-2 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-1 max-sm:flex-wrap max-sm:w-full">
                {['', 'info', 'warning', 'error', 'critical'].map((s) => (
                  <Button key={s} data-testid={`ops-log-filter-${s || 'all'}`} variant={severity === s ? 'default' : 'ghost'} size="sm"
                    className={`h-7 text-[11px] px-2 ${severity === s ? 'bg-[hsl(var(--ops-surface-2))] text-[hsl(var(--ops-text))]' : 'text-[hsl(var(--ops-text-muted))]'}`}
                    onClick={() => setSeverity(s)}>
                    {s ? s.toUpperCase() : 'ALL'}
                    {s && counts[s] ? <span className="ml-1 text-[9px] opacity-60">({counts[s]})</span> : null}
                  </Button>
                ))}
              </div>
              <div className="flex-1 min-w-[150px] max-w-[250px] max-sm:max-w-none relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-[hsl(var(--ops-text-muted))]" />
                <Input data-testid="ops-log-search-input" placeholder="Search logs..." value={search} onChange={(e) => setSearch(e.target.value)}
                  className="h-7 text-xs pl-7 bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]" />
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button data-testid="ops-logs-export-csv" variant="ghost" size="sm" className="h-7 text-[11px] px-2 gap-1 text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))]" onClick={exportCSV} disabled={loading || logs.length === 0}>
                  <Download className="size-3" /> Export CSV
                </Button>
                <div className="flex items-center gap-1.5">
                  <Switch id="ops-log-autoscroll" data-testid="ops-log-autoscroll-switch" checked={autoScroll} onCheckedChange={onAutoScrollChange} className="scale-75" />
                  <Label htmlFor="ops-log-autoscroll" data-testid="ops-log-autoscroll-label" className="inline-block w-[56px] text-[10px] text-[hsl(var(--ops-text-muted))] cursor-pointer">{autoScroll ? 'Auto-scroll' : 'Paused'}</Label>
                </div>
              </div>
            </div>
          </Card>

          {/* Log lines */}
          <Card data-testid="ops-log-viewer" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
            {loading ? (
              <div className="p-3 space-y-1">{Array.from({length:15}).map((_,i)=><Skeleton key={i} className="h-6 bg-[hsl(var(--ops-surface-2))]" />)}</div>
            ) : logs.length === 0 ? (
              <div className="px-3 py-12 text-center text-sm text-[hsl(var(--ops-text-muted))]">No logs match your filters</div>
            ) : (
              <div className="relative">
              <ScrollArea ref={scrollRef} data-testid="ops-log-scroll" className="h-[500px]">
                <div data-testid="ops-log-lines" className="font-mono-num text-[13px] sm:text-[12px] leading-[1.55] sm:leading-[1.5]">
                  {logs.map((log, i) => {
                    const cfg = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                    const SevIcon = cfg.icon;
                    const key = rowKeys[i];
                    const open = expanded.has(key);
                    const stripe = log.severity === 'critical' ? 'border-l-[hsl(var(--ops-critical))]' :
                      log.severity === 'error' ? 'border-l-[hsl(var(--ops-short))]' :
                      log.severity === 'warning' ? 'border-l-[hsl(var(--ops-warn))]' :
                      'border-l-transparent';
                    const ex = open ? explainLog(log) : null;
                    const tone = ex ? (TONE_CONFIG[ex.tone] || TONE_CONFIG.info) : null;
                    const ToneIcon = tone?.icon;
                    const panelId = `ops-log-explain-${i}`;
                    return (
                      <div key={key} data-log-key={key} className="border-b border-[hsl(var(--ops-border))]/40 last:border-b-0">
                      <div role="button" tabIndex={0} aria-expanded={open} aria-controls={open ? panelId : undefined}
                        data-testid="ops-log-row"
                        onClick={(e) => {
                          const sel = window.getSelection?.();
                          if (sel && !sel.isCollapsed && e.currentTarget.contains(sel.anchorNode)) return; // selecting text, not tapping
                          toggleLine(key, open);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLine(key, open); } }}
                        className={`flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2.5 px-3 py-2 sm:py-1.5 cursor-pointer hover:bg-[hsl(var(--ops-surface-2))] focus-visible:outline-none focus-visible:bg-[hsl(var(--ops-surface-2))] border-l-2 transition-colors duration-100 ${open ? 'bg-[hsl(var(--ops-surface-2))]/60' : ''} ${stripe}`}>
                        {/* Phone: one small muted line (time · level · source) above the message. Desktop: sm:contents turns these into columns. */}
                        <div data-testid="ops-log-meta" className="flex items-center gap-1.5 min-w-0 text-[11px] leading-4 text-[hsl(var(--ops-text-muted))] sm:contents">
                          <span className="whitespace-nowrap sm:shrink-0 sm:w-[138px] sm:pt-px">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' }) : '—'}
                          </span>
                          <Badge className={`rounded text-[9px] leading-[14px] border-0 px-1 py-0 shrink-0 sm:mt-[2px] ${cfg.bg} ${cfg.color}`}>
                            <SevIcon className="size-2.5 mr-0.5" />{cfg.label}
                          </Badge>
                          <span className="truncate min-w-0 sm:shrink-0 sm:w-[64px] sm:pt-px" title={log.source}>{log.source ? `[${log.source}]` : ''}</span>
                          <ChevronRight aria-hidden="true" className={`size-3.5 shrink-0 ml-auto sm:ml-0 sm:order-last sm:mt-[3px] text-[hsl(var(--ops-text-muted))] transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
                        </div>
                        <span data-testid="ops-log-msg" className="min-w-0 sm:flex-1 [font-variant-numeric:normal] [font-feature-settings:normal] text-[hsl(var(--ops-text))] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{log.message}</span>
                      </div>
                      {open && (
                        <div id={panelId} data-testid="ops-log-explain" className={`border-l-2 ${stripe} px-3 pb-2.5 pt-1 animate-in fade-in-0 slide-in-from-top-1 duration-150`}>
                          <div className="rounded-md border border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-surface-2))] px-3 py-2 space-y-1.5 text-[13px] leading-[1.5]">
                            <div className="flex items-center gap-1.5 font-semibold text-[hsl(var(--ops-text))]">
                              <ToneIcon className={`size-3.5 shrink-0 ${tone.color}`} />{ex.title}
                            </div>
                            <p className="text-[hsl(var(--ops-text))]">{ex.meaning}</p>
                            <p className="text-[hsl(var(--ops-text))]"><span className="text-[hsl(var(--ops-text-muted))]">What to do: </span>{ex.action}</p>
                            <div className="pt-1.5 border-t border-[hsl(var(--ops-border))]/60">
                              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ops-text-muted))]">Original line</div>
                              <div className="text-[11px] leading-4 text-[hsl(var(--ops-text-muted))] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{log.message}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              {showPill && (
                <button type="button" data-testid="ops-log-jump-latest" onClick={jumpToLatest}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium shadow-lg bg-[hsl(var(--ops-brand))] text-white hover:opacity-90 active:scale-95 transition">
                  <ArrowDown className="size-3" />
                  {newCount > 0 ? `New logs (${newCount})` : 'Jump to latest'}
                </button>
              )}
              </div>
            )}
          </Card>
        </div>

        {/* Alert sidebar */}
        <div className="space-y-3 max-lg:min-w-0">
          <Card data-testid="ops-alert-center" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
            <div className="px-3 py-2 border-b border-[hsl(var(--ops-border))] flex items-center justify-between">
              <span className="text-sm font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2">
                <AlertTriangle className="size-4 text-[hsl(var(--ops-warn))]" /> Alerts
              </span>
              <div className="flex gap-1">
                {alerts.filter(a => a.severity === 'critical').length > 0 && (
                  <Badge data-testid="ops-alert-badge-critical" className="rounded text-[9px] bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))] border-0">
                    {alerts.filter(a => a.severity === 'critical').length} CRIT
                  </Badge>
                )}
                {alerts.filter(a => a.severity === 'error').length > 0 && (
                  <Badge data-testid="ops-alert-badge-warning" className="rounded text-[9px] bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))] border-0">
                    {alerts.filter(a => a.severity === 'error').length} ERR
                  </Badge>
                )}
              </div>
            </div>
            <ScrollArea className="h-[440px]">
              {alerts.length === 0 ? (
                <div className="px-3 py-8 text-center text-xs text-[hsl(var(--ops-text-muted))] flex flex-col items-center gap-2">
                  <CheckCircle2 className="size-5 text-[hsl(var(--ops-long))]" />
                  No unacknowledged alerts
                </div>
              ) : (
                <div className="divide-y divide-[hsl(var(--ops-border))]/50">
                  {alerts.map((alert, i) => {
                    const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.error;
                    return (
                      <div key={i} className="px-3 py-2 hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-100">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <Badge className={`rounded text-[9px] border-0 px-1 ${cfg.bg} ${cfg.color} mb-1`}>{cfg.label}</Badge>
                            <p data-testid="ops-alert-msg" className="text-[11px] text-[hsl(var(--ops-text))] leading-snug max-lg:[overflow-wrap:anywhere]">{alert.message}</p>
                            <p className="text-[9px] text-[hsl(var(--ops-text-muted))] mt-0.5 font-mono-num">
                              {alert.timestamp ? new Date(alert.timestamp).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))] shrink-0"
                            onClick={() => ackAlert(alert.alert_id)}>ACK</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>
      </div>
    </div>
  );
}
